export const en = {
  title: 'Agent conversations',
  filters: {
    window: 'Time range',
    windows: { day: 'Last 24 hours', week: 'Last 7 days', month: 'Last 30 days' },
    keyword: 'Keyword',
    keywordPlaceholder: 'Scenario, run, conversation, user, or device ID',
    role: 'Agent',
    allRoles: 'All Agents',
    sort: 'Sort by',
    sorts: {
      last_activity_at: 'Last activity',
      call_count: 'Call count',
      total_tokens: 'Total tokens',
    },
    order: 'Order',
    orders: { desc: 'Descending', asc: 'Ascending' },
    search: 'Search',
    reset: 'Reset',
  },
  empty: {
    title: 'No Agent conversations',
    description: 'Start a new game and complete a turn to create a conversation record.',
  },
  list: {
    calls: '{count} model calls',
    tokens: 'Input {input} · Output {output} · Cache read {cached} · Cache write {cacheWrite}',
    identityUser: 'User {id}',
    identityGuest: 'Guest device {id}',
    lastActivity: 'Last activity {time}',
    open: 'View conversation',
  },
  roles: {
    kp: 'KP',
    npc: 'NPC',
    npc_action_kp: 'NPC action KP',
  },
  pagination: {
    summary: '{total} conversations · Page {page} of {pages}',
    pageSize: 'Per page',
    previous: 'Previous',
    next: 'Next',
  },
  detail: {
    title: 'Conversation details',
    loading: 'Loading conversation details',
    summary: '{scenario} · {role} · {turns} player inputs · {calls} model calls',
    roundsTitle: 'Player inputs and Agent replies',
    round: 'Turn {number}',
    playerInput: 'Player input',
    modes: { say: 'Dialogue', act: 'Action' },
    callsInRound: '{count} Agent replies',
    agentOutput: 'Agent reply {number}',
    system: 'System prompt',
    user: 'LLM user prompt',
    assistant: 'Assistant reply',
    messageNumber: 'Message {number}',
    rawJson: 'Raw JSON',
    requestContext: 'View this call request context',
    callDetails: 'Model call details',
    callTokens: 'Input {input} · Output {output} · Cache read {cached} · Cache write {cacheWrite}',
    requestId: 'Request ID',
    noRequestId: 'No LLM request ID recorded',
    intentTask: {
      title: 'Intent video task',
      occurrences: '{n} occurrences',
      intent: 'Normalized intent',
      openLogs: 'View task and {n} logs',
    },
    videoMatch: {
      title: 'Video match',
      status: { matched: 'Matched', unmatched: 'Not matched', not_evaluated: 'Not evaluated' },
      noReason: 'No additional decision reason was recorded.',
      open: 'View retrieval trace',
    },
  },
  videoMatch: {
    title: 'Video retrieval trace',
    titleWithTurn: 'Video retrieval trace · Turn {number}',
    back: 'Back to conversation',
    loading: 'Loading video retrieval trace',
    stepNumber: 'Step {number}',
    traceTitle: 'Executed retrieval path',
    logsTitle: 'Call logs',
    threshold: 'Selection threshold {value}',
    minimumThreshold: 'Minimum candidate threshold {value}',
    directThreshold: 'Direct-match threshold {value}',
    similarity: 'Similarity {value}',
    selectedCandidate: 'Selected',
    openCall: 'Open call {id}',
    openVideo: 'Open video',
    context: { scenario: 'Scenario', agent: 'Conversation', input: 'Player input' },
    result: {
      title: 'Final result',
      status: { matched: 'Matched', unmatched: 'Not matched', not_evaluated: 'Not evaluated' },
      noReason: 'No additional decision reason was recorded.',
      resource: 'Video resource',
      similarity: 'Final similarity',
    },
    methods: {
      exact_transition: 'Exact transition match',
      semantic_reuse: 'Semantic retrieval + model decision',
      intent_video: 'Intent vector fallback',
      none: 'No video selected',
    },
    capture: { complete: 'Complete trace', reconstructed: 'Historical reconstruction' },
    captureDescription: {
      complete: 'This turn stored the executed steps, candidate scores, threshold, model decision, and final result.',
      reconstructed: 'This turn predates candidate snapshots. The graph only shows steps proven by existing call logs and final turn data.',
    },
    steps: {
      historical_capture: 'Historical data boundary',
      cg_recall_gate: 'CG retrieval switch',
      smart_match_setting: 'Player video performance setting',
      exact_match: 'Exact transition lookup',
      saved_semantic_match: 'Saved semantic binding (legacy flow)',
      semantic_candidate_bucket: 'Semantic candidate bucket',
      semantic_embedding: 'Scene semantic embedding',
      semantic_vector_search: 'Scene vector search',
      semantic_rerank: 'LLM candidate decision',
      intent_embedding: 'Player input embedding',
      intent_vector_search: 'Intent vector search',
      intent_llm_selection: 'LLM video selection',
      intent_demand_embedding: 'Intent demand embedding',
      result: 'Final selection',
    },
    status: {
      passed: 'Passed',
      stopped: 'Stopped',
      selected: 'Selected',
      miss: 'Miss',
      available: 'Candidates available',
      empty: 'No candidates',
      ok: 'Succeeded',
      error: 'Failed',
      rejected: 'Rejected',
      needs_llm_selection: 'Requires model selection',
      below_minimum: 'Below candidate threshold',
      uncovered: 'Not covered',
      not_recorded: 'Not recorded',
      aborted: 'Aborted',
    },
    logs: {
      embeddingTitle: 'Embedding calls',
      embeddingDescription: 'Actual vector model calls linked to this turn.',
      noEmbedding: 'This retrieval path did not call an Embedding model.',
      modelDecisionTitle: 'Model decision calls',
      modelDecisionDescription: 'Candidate input and model output. Expand a call to inspect the original payload.',
      noModelDecision: 'This retrieval path did not call a decision model.',
      agentTitle: 'Conversation calls',
      agentDescription: 'Agent calls that produced this player turn context; they are shown as context, not retrieval steps.',
      callId: 'Call ID',
      operation: 'Operation ID',
      time: 'Time',
      duration: 'Duration',
      dimensions: 'Dimensions',
      tokens: 'Tokens',
      inputHash: 'Input hash',
      request: 'Model input',
      response: 'Model output',
      openFullCall: 'Open full call log',
    },
  },
}

export const zh = {
  title: 'Agent 对话',
  filters: {
    window: '时间范围',
    windows: { day: '最近 24 小时', week: '最近 7 天', month: '最近 30 天' },
    keyword: '关键词',
    keywordPlaceholder: '搜索剧本、游戏局、对话、用户或设备 ID',
    role: 'Agent',
    allRoles: '全部 Agent',
    sort: '排序字段',
    sorts: {
      last_activity_at: '最后活动时间',
      call_count: '调用次数',
      total_tokens: '令牌总量',
    },
    order: '排序方向',
    orders: { desc: '从高到低', asc: '从低到高' },
    search: '查询',
    reset: '重置',
  },
  empty: {
    title: '还没有 Agent 对话',
    description: '重新开始一局游戏并完成一轮后，这里会出现真实的对话记录。',
  },
  list: {
    calls: '{count} 次模型调用',
    tokens: '输入 {input} · 输出 {output} · 缓存读取 {cached} · 缓存写入 {cacheWrite}',
    identityUser: '用户 {id}',
    identityGuest: '游客设备 {id}',
    lastActivity: '最后活动 {time}',
    open: '查看对话',
  },
  roles: {
    kp: 'KP',
    npc: 'NPC',
    npc_action_kp: 'NPC 行为 KP',
  },
  pagination: {
    summary: '共 {total} 段对话 · 第 {page}/{pages} 页',
    pageSize: '每页数量',
    previous: '上一页',
    next: '下一页',
  },
  detail: {
    title: '对话详情',
    loading: '正在加载对话详情',
    summary: '{scenario} · {role} · {turns} 次玩家输入 · {calls} 次模型调用',
    roundsTitle: '玩家输入与 Agent 回复',
    round: '第 {number} 回合',
    playerInput: '玩家输入',
    modes: { say: '对话', act: '行动' },
    callsInRound: '{count} 次 Agent 回复',
    agentOutput: 'Agent 回复 {number}',
    system: '系统提示词',
    user: '模型用户提示词',
    assistant: 'Agent 回复',
    messageNumber: '第 {number} 条消息',
    rawJson: '原始 JSON',
    requestContext: '查看本次调用的请求上下文',
    callDetails: '模型调用详情',
    callTokens: '输入 {input} · 输出 {output} · 缓存读取 {cached} · 缓存写入 {cacheWrite}',
    requestId: '请求 ID',
    noRequestId: '未记录 LLM 请求 ID',
    intentTask: {
      title: '关联的意图视频任务',
      occurrences: '累计出现 {n} 次',
      intent: '归一化意图',
      openLogs: '查看任务和 {n} 条日志',
    },
    videoMatch: {
      title: '视频匹配',
      status: { matched: '已匹配', unmatched: '未匹配', not_evaluated: '未执行' },
      noReason: '没有记录额外判定原因',
      open: '查看召回链路',
    },
  },
  videoMatch: {
    title: '视频召回链路',
    titleWithTurn: '视频召回链路 · 第 {number} 回合',
    back: '返回对话',
    loading: '正在加载视频召回链路',
    stepNumber: '步骤 {number}',
    traceTitle: '本回合实际执行路径',
    logsTitle: '调用日志',
    threshold: '选中阈值 {value}',
    minimumThreshold: '候选有效线 {value}',
    directThreshold: '直接命中线 {value}',
    similarity: '相似度 {value}',
    selectedCandidate: '最终选中',
    openCall: '查看调用 {id}',
    openVideo: '打开视频',
    context: { scenario: '剧本', agent: '对话角色', input: '玩家原始输入' },
    result: {
      title: '最终结果',
      status: { matched: '已匹配', unmatched: '未匹配', not_evaluated: '未执行' },
      noReason: '没有记录额外判定原因',
      resource: '视频资源',
      similarity: '最终相似度',
    },
    methods: {
      exact_transition: '精准回合匹配',
      semantic_reuse: '场景语义召回与模型判定',
      intent_video: '玩家意图向量兜底',
      none: '没有选中视频',
    },
    capture: { complete: '完整链路', reconstructed: '历史还原' },
    captureDescription: {
      complete: '该回合已保存实际执行步骤、候选相似度、阈值、模型判定和最终结果。',
      reconstructed: '该回合产生时还没有候选快照；图中只展示现有调用日志和回合结果能够证明的步骤。',
    },
    steps: {
      historical_capture: '历史数据边界',
      cg_recall_gate: '视频召回总开关',
      smart_match_setting: '玩家视频演出设置',
      exact_match: '精准回合查询',
      saved_semantic_match: '历史语义绑定（旧流程）',
      semantic_candidate_bucket: '场景语义候选范围',
      semantic_embedding: '场景语义向量计算',
      semantic_vector_search: '场景向量召回',
      semantic_rerank: '模型候选判定',
      intent_embedding: '玩家原话向量计算',
      intent_vector_search: '意图视频向量召回',
      intent_llm_selection: '模型选片判定',
      intent_demand_embedding: '意图需求向量计算',
      result: '最终选片结果',
    },
    status: {
      passed: '通过',
      stopped: '已停止',
      selected: '已选中',
      miss: '未命中',
      available: '存在候选',
      empty: '没有候选',
      ok: '成功',
      error: '失败',
      rejected: '全部拒绝',
      needs_llm_selection: '进入模型选片',
      below_minimum: '低于候选有效线',
      uncovered: '未覆盖',
      not_recorded: '未记录',
      aborted: '已中止',
    },
    logs: {
      embeddingTitle: 'Embedding 调用',
      embeddingDescription: '与该回合直接关联的真实向量模型调用。',
      noEmbedding: '本次执行路径没有调用向量模型。',
      modelDecisionTitle: '模型判定调用',
      modelDecisionDescription: '模型收到的候选和原始输出；展开即可查看完整输入输出。',
      noModelDecision: '本次执行路径没有调用选片判定模型。',
      agentTitle: '对话模型调用',
      agentDescription: '这些调用生成了本回合上下文，只作为关联信息，不混入召回步骤。',
      callId: '调用编号',
      operation: '业务操作编号',
      time: '调用时间',
      duration: '耗时',
      dimensions: '向量维度',
      tokens: '令牌数',
      inputHash: '输入摘要',
      request: '模型输入',
      response: '模型输出',
      openFullCall: '查看完整调用日志',
    },
  },
}
